import { 
  ReviewClause, 
 
  NegotiationAnalysis, 

  ContractFavorabilityRating,
  CriticalIssue,
  PartyObligations,
  KeyTerms,
  DetailedRiskAnalysis,
  RiskDetail,
  NegotiationStrategy,
  ActionableRecommendations,
  RecommendationItem,

  ContractExecutiveSummary,
  DealBreaker,

  KeyRisk,
  PriorityAction,
  ContractScore
} from '../../types/contract.types';

export interface ParsedContractAnalysis {
  favorabilityRating: ContractFavorabilityRating;
  clauses: Partial<ReviewClause>[];
  executiveSummary?: ContractExecutiveSummary;
}

export function parseContractAnalysis(analysisText: string): ParsedContractAnalysis {
  const result: ParsedContractAnalysis = {
    favorabilityRating: {
      overallRating: 0,
      criticalIssues: []
    },
    clauses: []
  };

  // Parse overall rating
  const ratingMatch = analysisText.match(/OVERALL CONTRACT FAVORABILITY RATING:\s*(\d+(?:\.\d+)?)\s*\/\s*10/i);
  if (ratingMatch) {
    result.favorabilityRating.overallRating = parseFloat(ratingMatch[1]);
  }

  // Parse executive summary
  const execSummaryMatch = analysisText.match(/EXECUTIVE SUMMARY[^⚠️]*(⚠️🔥[\s\S]*?)(?=⸻|CLAUSE-BY-CLAUSE|$)/);
  if (execSummaryMatch) {
    const summarySection = execSummaryMatch[1];
    const issues = summarySection.split(/\d+\.\s*⚠️/).slice(1);
    
    (result.favorabilityRating.criticalIssues as any) = issues.map(issue => {
      const lines = issue.trim().split('\n');
      const titleLine = lines[0];
      const descriptionLines = lines.slice(1);
      
      // Extract negotiation priority
      const priorityMatch = issue.match(/🔥\s*Negotiation Priority:\s*([^.]+)/);
      
      return {
        title: titleLine.replace(/\([^)]+\)/, '').trim(),
        description: descriptionLines
          .filter(line => !line.includes('Negotiation Priority'))
          .join(' ')
          .trim(),
        priority: issue.includes('🔥') ? 'critical' : 'high',
        negotiationPriority: priorityMatch ? priorityMatch[1].trim() : undefined
      } as any;
    });
  }

  // Extract summary text after rating
  const summaryMatch = analysisText.match(/This agreement[\s\S]*?(?=⸻|$)/);
  if (summaryMatch) {
    result.favorabilityRating.executiveSummary = summaryMatch[0].trim();
  }

  // Parse clauses
  const clauseSections = analysisText.split(/Clause Reference:\s*/);
  
  console.log(`Found ${clauseSections.length - 1} clause sections to parse`);
  
  for (let i = 1; i < clauseSections.length; i++) {
    const section = clauseSections[i];
    const clause: Partial<ReviewClause> = {
      negotiationHistory: []
    };

    console.log(`\n=== Parsing clause ${i} ===`);

    // Extract section reference
    const sectionMatch = section.match(/^([^\n]+)/);
    if (sectionMatch) {
      clause.sectionReference = sectionMatch[1].trim();
      console.log('Section Reference:', clause.sectionReference);
    }

    // Extract title
    const titleMatch = section.match(/•\s*Title:\s*([^\n]+)/);
    if (titleMatch) {
      clause.clauseTitle = titleMatch[1].trim();
    } else {
      // Fallback: try to extract from the section header
      const headerMatch = section.match(/^([^\n]+)\n\n1\.\s*CLAUSE IDENTIFICATION/);
      if (headerMatch) {
        clause.clauseTitle = headerMatch[1].trim();
      }
    }
    console.log('Title:', clause.clauseTitle);

    // Extract full clause text and obligations
    const clauseIdMatch = section.match(/1\.\s*CLAUSE IDENTIFICATION[\s\S]*?(?=2\.\s*RISK|$)/);
    if (clauseIdMatch) {
      const clauseIdText = clauseIdMatch[0];
      
      // Extract full clause content from Obligations section
      const obligationsMatch = clauseIdText.match(/•\s*Obligations?:\s*([\s\S]*?)(?=2\.\s*RISK|$)/);
      if (obligationsMatch) {
        clause.clauseContent = obligationsMatch[1].trim();
        clause.fullClauseText = obligationsMatch[1].trim();
        console.log('Obligations found:', clause.clauseContent.substring(0, 50) + '...');
      }

      // Parse party obligations
      clause.obligations = parsePartyObligations(clauseIdText);
      
      // Extract key terms
      clause.keyTerms = parseKeyTerms(section);
    }

    // Extract detailed risk analysis
    const riskMatch = section.match(/2\.\s*RISK ASSESSMENT[\s\S]*?(?=3\.\s*NEGOTIATION|$)/);
    if (riskMatch) {
      const riskText = riskMatch[0];
      console.log('Risk Assessment section found');
      
      // Basic risk assessment for backward compatibility
      clause.riskAssessment = {
        legal: extractRiskLevel(riskText, 'Legal'),
        financial: extractRiskLevel(riskText, 'Financial'),
        operational: extractRiskLevel(riskText, 'Operational'),
        regulatory: extractRiskLevel(riskText, 'Regulatory')
      };
      
      // Detailed risk analysis
      clause.detailedRiskAnalysis = parseDetailedRiskAnalysis(riskText);
      console.log('Risk levels:', clause.riskAssessment);
      console.log('Detailed risk analysis:', Object.keys(clause.detailedRiskAnalysis || {}));
    }

    // Extract negotiation analysis and strategy
    const negotiationMatch = section.match(/3\.\s*NEGOTIATION ANALYSIS[\s\S]*?(?=4\.\s*ACTIONABLE|$)/);
    if (negotiationMatch) {
      const negotiationText = negotiationMatch[0];
      console.log('Negotiation Analysis section found');
      
      // Basic negotiation analysis
      clause.negotiationAnalysis = parseNegotiationAnalysis(negotiationText);
      console.log('Negotiation favorability:', clause.negotiationAnalysis?.favorability);
      
      // Detailed negotiation strategy
      clause.negotiationStrategy = parseNegotiationStrategy(negotiationText, section);
      console.log('Negotiation strategy priority:', clause.negotiationStrategy?.priorityLevel);
    }

    // Extract classification
    const classificationMatch = section.match(/•\s*Classification:\s*🔥?\s*([^\n]+)/);
    if (classificationMatch) {
      const classification = classificationMatch[1].toLowerCase();
      if (classification.includes('deal-breaker') || classification.includes('deal breaker')) {
        clause.classification = 'deal_breaker';
      } else if (classification.includes('negotiable')) {
        clause.classification = 'negotiable';
      } else if (classification.includes('favorable')) {
        clause.classification = 'favorable';
      } else {
        clause.classification = 'standard';
      }
      console.log('Classification:', clause.classification);
    }

    // Extract actionable recommendations
    const recsMatch = section.match(/4\.\s*ACTIONABLE RECOMMENDATIONS\s*✅[\s\S]*?(?=⸻|Clause Reference:|$)/);
    if (recsMatch) {
      console.log('Recommendations section found');
      clause.recommendations = parseSimpleRecommendations(recsMatch[0]);
      clause.actionableRecommendations = parseDetailedRecommendations(recsMatch[0]);
      console.log('Recommendations count:', clause.recommendations?.length);
    }

    // Extract category from section
    const categoryMatch = section.match(/•\s*Section:\s*([^\n]+)/);
    if (categoryMatch) {
      const cat = categoryMatch[1].trim();
      clause.category = mapToClauseCategory(cat);
    }

    // Set default values
    clause.status = 'pending';
    clause.priority = determineClausePriority(clause);
    clause.comments = '';
    clause.reviewedBy = 'AI Analysis';

    result.clauses.push(clause);
  }

  // Create executive summary
  result.executiveSummary = createExecutiveSummary(result.clauses, result.favorabilityRating);

  return result;
}

function extractRiskLevel(text: string, riskType: string): 'high' | 'medium' | 'low' | 'none' {
  // Match bullet point format: • Legal Risk: ⚠️ High – explanation
  const bulletRegex = new RegExp(`•\\s*${riskType}(?:\\s+Risk)?:\\s*(?:⚠️\\s*)?(High|Medium|Low|None)`, 'i');
  let match = text.match(bulletRegex);
  
  if (match) {
    return match[1].toLowerCase() as 'high' | 'medium' | 'low' | 'none';
  }
  
  // Fallback to old format
  const regex = new RegExp(`${riskType}:?\\s*(?:Risk:?)?\\s*⚠️?\\s*(High|Medium|Low|None)`, 'i');
  match = text.match(regex);
  
  if (match) {
    return match[1].toLowerCase() as 'high' | 'medium' | 'low' | 'none';
  }
  
  // Check for emoji indicators
  if (text.includes(riskType) && text.includes('⚠️')) {
    return 'high';
  }
  
  return 'medium';
}

function mapToClauseCategory(text: string): any {
  const categoryMap: Record<string, any> = {
    'pricing': 'Pricing',
    'payment': 'Payment',
    'liability': 'Liability',
    'termination': 'Termination',
    'intellectual property': 'Intellectual Property',
    'warranty': 'Warranty',
    'confidentiality': 'Confidentiality',
    'dispute': 'Dispute Resolution',
    'force majeure': 'Force Majeure'
  };

  const lowerText = text.toLowerCase();
  for (const [key, value] of Object.entries(categoryMap)) {
    if (lowerText.includes(key)) {
      return value;
    }
  }
  
  return 'Terms';
}

function determineClausePriority(clause: Partial<ReviewClause>): 'high' | 'medium' | 'low' {
  if (clause.classification === 'deal_breaker') return 'high';
  
  if (clause.riskAssessment) {
    const { legal, financial, operational, regulatory } = clause.riskAssessment;
    if (legal === 'high' || financial === 'high' || operational === 'high' || regulatory === 'high') {
      return 'high';
    }
    if (legal === 'medium' || financial === 'medium' || operational === 'medium' || regulatory === 'medium') {
      return 'medium';
    }
  }
  
  if (clause.negotiationAnalysis?.favorability === 'strongly_favors_counterparty') {
    return 'high';
  }
  
  return 'medium';
} 

function parsePartyObligations(text: string): PartyObligations {
  const obligations: PartyObligations = {
    ourObligations: [],
    theirObligations: [],
    mutualObligations: []
  };

  // Look for distributor/buyer obligations
  const distributorMatch = text.match(/Distributor\s+(?:must|shall|will)[^.]+\./gi);
  if (distributorMatch) {
    obligations.ourObligations = distributorMatch.map(o => o.trim());
  }

  // Look for supplier/vendor obligations
  const supplierMatch = text.match(/(?:Supplier|Vendor|Roja)\s+(?:must|shall|will)[^.]+\./gi);
  if (supplierMatch) {
    obligations.theirObligations = supplierMatch.map(o => o.trim());
  }

  // Look for mutual obligations
  const mutualMatch = text.match(/(?:Both parties|Each party|Parties)\s+(?:must|shall|will)[^.]+\./gi);
  if (mutualMatch) {
    obligations.mutualObligations = mutualMatch.map(o => o.trim());
  }

  return obligations;
}

function parseKeyTerms(text: string): KeyTerms {
  const keyTerms: KeyTerms = {
    dates: [],
    amounts: [],
    thresholds: [],
    percentages: []
  };

  // Extract dates
  const dateMatches = text.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{2,4}\b|\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/gi);
  if (dateMatches) {
    keyTerms.dates = dateMatches.map(d => ({ label: 'Date', value: d }));
  }

  // Extract amounts
  const amountMatches = text.match(/(?:USD|GBP|EUR|[$£€])\s*[\d,]+(?:\.\d{2})?/gi);
  if (amountMatches) {
    keyTerms.amounts = amountMatches.map(a => {
      const value = parseFloat(a.replace(/[^0-9.]/g, ''));
      const currency = a.includes('USD') ? 'USD' : a.includes('GBP') ? 'GBP' : a.includes('EUR') ? 'EUR' : '$';
      return { label: 'Amount', value, currency };
    });
  }

  // Extract percentages
  const percentMatches = text.match(/\d+(?:\.\d+)?%/g);
  if (percentMatches) {
    keyTerms.percentages = percentMatches.map(p => ({
      label: 'Percentage',
      value: parseFloat(p.replace('%', ''))
    }));
  }

  return keyTerms;
}

function parseDetailedRiskAnalysis(riskText: string): DetailedRiskAnalysis {
  const categories = ['legal', 'financial', 'operational', 'regulatory'];
  const analysis: any = {};

  categories.forEach(category => {
    // Match bullet point format: • Legal Risk: ⚠️ High – explanation text
    const bulletRegex = new RegExp(`•\\s*${category}(?:\\s+Risk)?:\\s*(?:⚠️\\s*)?(High|Medium|Low|None)\\s*[–-]\\s*([^•\n]+(?:\n(?!•)[^\n]+)*)`, 'si');
    const match = riskText.match(bulletRegex);
    
    if (match) {
      const riskDetail: RiskDetail = {
        level: match[1].toLowerCase() as any,
        explanation: match[2].trim(),
        specificScenarios: [],
        mitigationStrategies: [],
        marketComparison: ''
      };

      // Extract specific scenarios from the explanation
      if (riskDetail.explanation.includes('trigger')) {
        riskDetail.specificScenarios.push(riskDetail.explanation);
      }
      
      // Look for mitigation strategies in the full text
      const mitigationRegex = new RegExp(`${category}[^:]*mitigation[^:]*:([^.]+)`, 'i');
      const mitigationMatch = riskText.match(mitigationRegex);
      if (mitigationMatch) {
        riskDetail.mitigationStrategies = [mitigationMatch[1].trim()];
      }

      // Extract potential impact if mentioned
      if (riskDetail.explanation.includes('USD') || riskDetail.explanation.includes('GBP') || riskDetail.explanation.includes('EUR')) {
        const amountMatch = riskDetail.explanation.match(/(?:USD|GBP|EUR|[$£€])\s*[\d,]+(?:\.\d{2})?/);
        if (amountMatch) {
          const amount = parseFloat(amountMatch[0].replace(/[^0-9.]/g, ''));
          riskDetail.potentialImpact = {
            minAmount: amount,
            description: `Financial exposure of ${amountMatch[0]}`
          };
        }
      }

      analysis[category] = riskDetail;
    }
  });

  return analysis as DetailedRiskAnalysis;
}

function parseNegotiationAnalysis(text: string): NegotiationAnalysis {
  const analysis: NegotiationAnalysis = {
    favorability: 'neutral'
  };

  // Extract favorability with bullet point format
  const favorabilityMatch = text.match(/•\s*Favorability:\s*([^\n]+)/);
  if (favorabilityMatch) {
    const fav = favorabilityMatch[1].toLowerCase();
    if (fav.includes('strongly') && fav.includes('counterparty')) {
      analysis.favorability = 'strongly_favors_counterparty';
    } else if (fav.includes('counterparty')) {
      analysis.favorability = 'favors_counterparty';
    } else if (fav.includes('us') || fav.includes('our')) {
      analysis.favorability = 'favors_us';
    }
  }

  // Extract market standard with bullet point format
  const marketMatch = text.match(/•\s*Market Standard:\s*([^\n]+)/);
  if (marketMatch) {
    analysis.marketStandard = marketMatch[1].trim();
  }

  // Extract recommended modifications with bullet point format
  const modMatch = text.match(/•\s*🔥\s*Recommended Modifications?:\s*\n?"([^"]+)"/);
  if (modMatch) {
    analysis.recommendedModifications = modMatch[1].trim();
  } else {
    // Try without quotes
    const modMatchNoQuotes = text.match(/•\s*🔥\s*Recommended Modifications?:\s*([^\n]+)/);
    if (modMatchNoQuotes) {
      analysis.recommendedModifications = modMatchNoQuotes[1].trim();
    }
  }

  return analysis;
}

function parseNegotiationStrategy(negotiationText: string, fullSection: string): NegotiationStrategy {
  const strategy: NegotiationStrategy = {
    priorityLevel: 'should_negotiate',
    redlineLanguage: [],
    alternativeOptions: [],
    industryBenchmarks: [],
    negotiationTactics: [],
    walkAwayTriggers: []
  };

  // Determine priority level based on classification
  if (fullSection.includes('Deal-breaker') || fullSection.includes('🔥 Deal-breaker')) {
    strategy.priorityLevel = 'must_negotiate';
  } else if (fullSection.includes('Negotiable')) {
    strategy.priorityLevel = 'should_negotiate';
  } else if (fullSection.includes('Favorable')) {
    strategy.priorityLevel = 'acceptable';
  }

  // Extract redline language
  const redlineMatch = negotiationText.match(/Recommended Modifications?:\s*"([^"]+)"/);
  if (redlineMatch) {
    strategy.redlineLanguage = [redlineMatch[1]];
  }

  // Extract industry benchmarks
  const benchmarkMatch = negotiationText.match(/Market Standard:\s*([^\n]+)/);
  if (benchmarkMatch) {
    strategy.industryBenchmarks = [benchmarkMatch[1]];
  }

  // Look for walk-away triggers
  if (fullSection.includes('immediate termination') || fullSection.includes('unilateral')) {
    if (strategy.walkAwayTriggers) {
      strategy.walkAwayTriggers.push('Unilateral termination rights without cure period');
    }
  }

  return strategy;
}

function parseSimpleRecommendations(text: string): string[] {
  const recommendations: string[] = [];
  const bulletPoints = text.match(/•\s*([^•\n]+(?:\n(?!•)[^\n]+)*)/g);
  
  if (bulletPoints) {
    return bulletPoints.map(rec => rec.replace(/•\s*/, '').trim());
  }
  
  return recommendations;
}

function parseDetailedRecommendations(text: string): ActionableRecommendations {
  const recommendations: ActionableRecommendations = {
    immediateActions: [],
    negotiationPriorities: [],
    alternativeLanguage: [],
    protectiveAdditions: [],
    implementationRequirements: []
  };

  // Parse all bullet points - updated regex to match the actual format
  const bulletPoints = text.match(/•\s*([^•\n]+(?:\n(?!•)[^\n]+)*)/g) || [];
  
  bulletPoints.forEach(point => {
    const cleanPoint = point.replace(/•\s*/, '').trim();
    const item: RecommendationItem = {
      action: cleanPoint,
      rationale: 'Based on contract analysis',
      priority: 'medium'
    };

    // Categorize based on content
    if (cleanPoint.toLowerCase().includes('immediate') || cleanPoint.toLowerCase().includes('before signing')) {
      item.priority = 'high';
      recommendations.immediateActions.push(item);
    } else if (cleanPoint.toLowerCase().includes('negotiate') || cleanPoint.toLowerCase().includes('modify') || cleanPoint.toLowerCase().includes('request')) {
      recommendations.negotiationPriorities.push(item);
    } else if (cleanPoint.toLowerCase().includes('language') || cleanPoint.toLowerCase().includes('clause')) {
      recommendations.alternativeLanguage.push(item);
    } else if (cleanPoint.toLowerCase().includes('add') || cleanPoint.toLowerCase().includes('insert') || cleanPoint.toLowerCase().includes('secure')) {
      recommendations.protectiveAdditions.push(item);
    } else {
      recommendations.implementationRequirements.push(item);
    }
  });

  return recommendations;
}

function createExecutiveSummary(clauses: Partial<ReviewClause>[], favorabilityRating: ContractFavorabilityRating): ContractExecutiveSummary {
  const dealBreakers: DealBreaker[] = [];
  const keyRisks: KeyRisk[] = [];
  const priorityActions: PriorityAction[] = [];
  
  // Extract deal breakers
  clauses.forEach(clause => {
    if (clause.classification === 'deal_breaker' && clause.clauseTitle) {
      dealBreakers.push({
        clauseId: clause.clauseId || '',
        issue: clause.clauseTitle,
        impact: clause.clauseContent || '',
        recommendation: clause.negotiationAnalysis?.recommendedModifications || 'Requires immediate negotiation',
        severity: 'critical'
      });
    }

    // Extract key risks
    if (clause.detailedRiskAnalysis) {
      Object.entries(clause.detailedRiskAnalysis).forEach(([category, detail]) => {
        if (detail && detail.level === 'high') {
          keyRisks.push({
            category: category.charAt(0).toUpperCase() + category.slice(1),
            description: detail.explanation,
            likelihood: 'high',
            impact: 'high',
            mitigation: detail.mitigationStrategies[0] || 'Requires mitigation strategy'
          });
        }
      });
    }
  });

  // Create priority actions from critical issues
  favorabilityRating.criticalIssues?.forEach((issue, index) => {
    priorityActions.push({
      rank: index + 1,
      action: issue.title,
      rationale: issue.description,
      requiredApprovals: issue.priority === 'critical' ? ['Legal', 'Executive'] : ['Legal']
    });
  });

  // Calculate contract scores
  const overallScore = favorabilityRating.overallRating || 5;
  const contractScore: ContractScore = {
    overall: overallScore,
    commercial: overallScore - 0.5,
    legal: overallScore - 1,
    operational: overallScore + 0.5,
    strategic: overallScore,
    explanation: favorabilityRating.executiveSummary
  };

  return {
    contractId: '0', // This will be set by the caller
    overallAssessment: favorabilityRating.executiveSummary || 'Contract requires significant negotiation',
    dealBreakers,
    financialSummary: {
      paymentTerms: '30 days net',
      liabilityCaps: { ours: 'Unlimited', theirs: 'Limited' }
    },
    keyRisks,
    priorityActions,
    contractScore,
    missingProtections: [],
    unusualProvisions: []
  };
} 